import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Menu, LogOut, Home } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

export default function DashboardLayout({ nav, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Safeguard: Radix overlays (Select/Dialog/Popover) set `pointer-events:none`
  // on <body>. If the component unmounts while an overlay is still open (e.g. the
  // user navigates via the sidebar with a dropdown open), Radix never restores it,
  // leaving the whole panel — sidebar included — unclickable. Reset on every route change.
  useEffect(() => {
    const unlock = () => {
      document.body.style.pointerEvents = "";
      document.body.style.removeProperty("overflow");
      document.body.removeAttribute("data-scroll-locked");
    };
    unlock();
    const t = setTimeout(unlock, 80);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Also close the mobile sheet whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => {
        const active = location.pathname === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onClick}
            className={`group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              active ? "bg-primary/10 text-primary shadow-sm" : "text-muted-foreground hover:translate-x-0.5 hover:bg-accent/60 hover:text-foreground"
            }`}
            data-testid={`sidebar-${item.to.split("/").pop() || "home"}`}
          >
            {active && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />}
            <item.icon className={`h-4 w-4 transition-transform duration-200 ${active ? "" : "group-hover:scale-110"}`} /> {item.label}
          </Link>
        );
      })}
    </nav>
  );

  const Sidebar = ({ onClick }) => (
    <div className="flex h-full flex-col bg-gradient-to-b from-card/50 via-background to-background">
      <div className="px-3 py-5"><Logo /></div>
      <div className="flex-1 overflow-y-auto px-3">
        <NavItems onClick={onClick} />
      </div>
      <div className="border-t border-border/70 p-3">
        <Link to="/" onClick={onClick} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground" data-testid="sidebar-back-home">
          <Home className="h-4 w-4" /> Ir para o site
        </Link>
        <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" data-testid="sidebar-logout">
          <LogOut className="h-4 w-4" /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 border-r border-border/70 md:block">
        <div className="sticky top-0 h-screen"><Sidebar /></div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-2 border-b border-border glass bg-background/70 px-3 sm:h-20 sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0 md:hidden" data-testid="dash-mobile-menu"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[17rem] max-w-[85vw] p-0">
                <Sidebar onClick={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="truncate font-heading text-base font-semibold sm:text-lg" data-testid="dash-title">{title}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {user && user !== false && (
              <Badge variant="outline" className="hidden sm:inline-flex border-primary/30 bg-primary/10 capitalize text-primary" data-testid="dash-role-badge">{user.role}</Badge>
            )}
            <ThemeToggle />
          </div>
        </header>

        <main className="pb-nav-safe min-w-0 flex-1 p-4 sm:p-6 md:pb-8 lg:p-8">
          <Outlet />
        </main>

        {/* Mobile bottom navigation */}
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border bg-background/95 backdrop-blur-xl md:hidden" data-testid="mobile-bottom-nav">
          {nav.slice(0, 3).map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`bottomnav-${item.to.split("/").pop() || "home"}`}
                className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-2.5 text-[10px] font-medium transition-colors duration-200 active:scale-95 ${active ? "text-primary" : "text-muted-foreground"}`}
              >
                <item.icon className="h-5 w-5 shrink-0" /> <span className="w-full truncate text-center">{item.label.split(" ")[0]}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setOpen(true)}
            data-testid="bottomnav-menu"
            className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-2.5 text-[10px] font-medium text-muted-foreground transition-colors duration-200 active:scale-95"
          >
            <Menu className="h-5 w-5 shrink-0" /> Menu
          </button>
        </nav>
      </div>
    </div>
  );
}
